import React, { useEffect } from 'react'
import { connect } from 'react-redux'

import DisplayValue from '../../../utils/display-value'

import moment from 'moment'

import { fetchApiDataIfNeeded } from '../../../../actions/api'
import { apiResourceEndpoint } from '../../../../constants/routes'
import { formatDate, formatTime, valueFromConfig } from '../../../utils/data-formatters'
import {
    DATE_FLEXIBILITY_OPTIONS, NEEDS_VENUE_OPTIONS, NEEDS_BARTENDER_OPTIONS, KITCHEN_MANAGEMENT_OPTIONS,
    DELIVERY_TYPE_OPTIONS, MEAL_TIME_OPTIONS, MEAL_STYLE_OPTIONS, optionLabel
} from '../../../../constants/inquiry-options'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

const mapStateToProps = (state) => ({
    apiCuisines: state.apiCuisines,
    apiServiceStyles: state.apiServiceStyles,
    apiVibes: state.apiVibes,
    apiOccasions: state.apiOccasions,
    apiAllergens: state.apiAllergens,
    apiDietaryRestrictions: state.apiDietaryRestrictions
})

const formatOption = (val) => {
    if (val === null || val === undefined || val === '') {
        return null
    }
    if (val === true) {
        return 'Yes'
    }
    if (val === false) {
        return 'No'
    }
    return String(val).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

const safeFormatTime = (time) => {
    if (!time) {
        return null
    }
    return formatTime(time)
}

const LeadEventInfo = (props) => {
    const { leadForm, dispatch, apiCuisines, apiServiceStyles, apiVibes, apiOccasions, apiAllergens, apiDietaryRestrictions } = props

    useEffect(() => {
        dispatch(fetchApiDataIfNeeded(apiResourceEndpoint('public/api-cuisines', 'LIST')))
        dispatch(fetchApiDataIfNeeded(apiResourceEndpoint('public/api-service-styles', 'LIST')))
        dispatch(fetchApiDataIfNeeded(apiResourceEndpoint('public/api-vibes', 'LIST')))
        dispatch(fetchApiDataIfNeeded(apiResourceEndpoint('public/api-occasions', 'LIST')))
        dispatch(fetchApiDataIfNeeded(apiResourceEndpoint('public/api-allergens', 'LIST')))
        dispatch(fetchApiDataIfNeeded(apiResourceEndpoint('public/api-dietary-restrictions', 'LIST')))
    }, [dispatch])

    // 'unsure' is a guest-only choice on the inquiry form, so it has no service_styles row.
    const serviceStyleName = () => {
        if (leadForm.apiServiceStyleId === 'unsure') {
            return 'Unsure'
        }
        return valueFromConfig(apiServiceStyles, leadForm.apiServiceStyleId, 'name')
    }

    const renderAddress = () => {
        const parts = [leadForm.street, leadForm.city, leadForm.state, leadForm.postal].filter(Boolean)
        return parts.join(', ') || null
    }

    const resolveList = (ids, source) => {
        if (!ids || ids.length === 0) {
            return null
        }
        return ids.map(id => {
            const found = source.find(o => o.id === id || o.id === Number(id))
            return found ? found.name : id
        }).join(', ')
    }

    const renderMealSchedule = (schedule) => {
        if (!schedule || schedule.length === 0) {
            return null
        }
        return (
            <ul className="lead-meal-schedule">
                {schedule.map((day, i) => (
                    <li key={i}>
                        {moment(day.date).format('dddd, MMMM D')}
                        <span>{(day.meals || []).map(m => {
                            const style = (day.styles || {})[m]
                            const meal = m.charAt(0).toUpperCase() + m.slice(1)
                            return style ? `${meal} (${optionLabel(MEAL_STYLE_OPTIONS, style)})` : meal
                        }).join(', ')}</span>
                    </li>
                ))}
            </ul>
        )
    }

    const renderPrivateDinnerBlock = () => (
        <div className="event-block">
            <div className="block-type">
                <FontAwesomeIcon icon={['far', 'calendar-alt']} /> Event Info
            </div>
            <div className="content">
                <div className="displays">
                    <DisplayValue label="Event Name" value={leadForm.eventName} extraClass="two-thirds" showNull={true} defaultNullValue="—" />
                    <DisplayValue label="Headcount" value={leadForm.headcount} extraClass="one-third" showNull={true} defaultNullValue="—" />
                    <DisplayValue label="Date" value={formatDate(leadForm.eventDate, true)} extraClass="two-thirds" showNull={true} defaultNullValue="—" />
                    <DisplayValue label="Time" value={safeFormatTime(leadForm.eventTime)} extraClass="one-third" showNull={true} defaultNullValue="—" />
                    <DisplayValue label="Date Flexibility" value={optionLabel(DATE_FLEXIBILITY_OPTIONS, leadForm.dateFlexibility)} extraClass="one-third" showNull={true} defaultNullValue="—" />
                    <DisplayValue label="Needs Venue" value={optionLabel(NEEDS_VENUE_OPTIONS, leadForm.needsVenue)} extraClass="one-third" showNull={true} defaultNullValue="—" />
                    <DisplayValue label="Why They're Hosting" value={valueFromConfig(apiOccasions, leadForm.apiOccasionId, 'name')} extraClass="one-third" showNull={true} defaultNullValue="—" />
                    <DisplayValue label="Location" value={renderAddress()} showNull={true} defaultNullValue="—" />
                    <DisplayValue label="Who Is Coming" value={leadForm.guestDescription} showNull={true} defaultNullValue="—" />
                    <DisplayValue label="Needs Cleanup" value={formatOption(leadForm.needsCleanup)} extraClass="one-third" showNull={true} defaultNullValue="—" />
                    <DisplayValue label="Needs Rentals" value={formatOption(leadForm.needsRentals)} extraClass="one-third" showNull={true} defaultNullValue="—" />
                    <DisplayValue label="First Time" value={formatOption(leadForm.isFirstTime)} extraClass="one-third" showNull={true} defaultNullValue="—" />
                    <DisplayValue label="Has Gift Card" value={formatOption(leadForm.hasGiftCard)} extraClass="one-third" showNull={true} defaultNullValue="—" />
                </div>
            </div>
            <div className="actions">&nbsp;</div>
        </div>
    )

    const renderMultiDayBlock = () => (
        <div className="event-block">
            <div className="block-type">
                <FontAwesomeIcon icon={['far', 'calendar-alt']} /> Event Info
            </div>
            <div className="content">
                <div className="displays">
                    <DisplayValue label="Event Name" value={leadForm.eventName} extraClass="two-thirds" showNull={true} defaultNullValue="—" />
                    <DisplayValue label="Headcount" value={leadForm.headcount} extraClass="one-third" showNull={true} defaultNullValue="—" />
                    <DisplayValue
                        label="Dates"
                        value={leadForm.eventDate ? `${moment(leadForm.eventDate).format('dddd, MMMM D, YYYY')} to ${moment(leadForm.endDate || leadForm.eventDate).format('dddd, MMMM D, YYYY')}` : null}
                        showNull={true}
                        defaultNullValue="—" />
                    <div className="display-value">
                        <span className="label">Meal Schedule</span>
                        <span className="value">{renderMealSchedule(leadForm.mealSchedule) || '—'}</span>
                    </div>
                    <DisplayValue label="Needs Venue" value={optionLabel(NEEDS_VENUE_OPTIONS, leadForm.needsVenue)} extraClass="half" showNull={true} defaultNullValue="—" />
                    <DisplayValue label="Occasion" value={valueFromConfig(apiOccasions, leadForm.apiOccasionId, 'name')} extraClass="half" showNull={true} defaultNullValue="—" />
                    <DisplayValue label="Location" value={renderAddress()} showNull={true} defaultNullValue="—" />
                    <DisplayValue label="Hosting Reason" value={leadForm.hostingReason} showNull={true} defaultNullValue="—" />
                    <DisplayValue label="Needs Rentals" value={formatOption(leadForm.needsRentals)} extraClass="one-third" showNull={true} defaultNullValue="—" />
                    <DisplayValue label="First Time" value={formatOption(leadForm.isFirstTime)} extraClass="one-third" showNull={true} defaultNullValue="—" />
                    <DisplayValue label="Has Gift Card" value={formatOption(leadForm.hasGiftCard)} extraClass="one-third" showNull={true} defaultNullValue="—" />
                </div>
            </div>
            <div className="actions">&nbsp;</div>
        </div>
    )

    const renderWeeklyMealBlock = () => (
        <div className="event-block">
            <div className="block-type">
                <FontAwesomeIcon icon={['far', 'calendar-alt']} /> Schedule
            </div>
            <div className="content">
                <div className="displays">
                    <DisplayValue label="Start Date" value={formatDate(leadForm.eventDate, true)} showNull={true} defaultNullValue="—" />
                    <DisplayValue label="Days of Week" value={(leadForm.daysOfWeek || []).map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(', ') || null} showNull={true} defaultNullValue="—" />
                    <DisplayValue label="Meal Times" value={(leadForm.mealTimes || []).map(m => optionLabel(MEAL_TIME_OPTIONS, m)).join(', ') || null} showNull={true} defaultNullValue="—" />
                    <DisplayValue label="Service Type" value={optionLabel(DELIVERY_TYPE_OPTIONS, leadForm.deliveryType)} extraClass="half" showNull={true} defaultNullValue="—" />
                    <DisplayValue label="Portions per Meal" value={leadForm.headcount} extraClass="half" showNull={true} defaultNullValue="—" />
                    <DisplayValue label="Location" value={renderAddress()} showNull={true} defaultNullValue="—" />
                </div>
            </div>
            <div className="actions">&nbsp;</div>
        </div>
    )

    const renderLegacyBlock = () => (
        <div className="event-block">
            <div className="block-type">
                <FontAwesomeIcon icon={['far', 'calendar-alt']} /> Event Info
            </div>
            <div className="content">
                <div className="displays">
                    <DisplayValue label="Event Name" value={leadForm.eventName} extraClass="two-thirds" showNull={true} defaultNullValue="—" />
                    <DisplayValue label="Headcount" value={leadForm.headcount} extraClass="one-third" showNull={true} defaultNullValue="—" />
                    <DisplayValue label="Date" value={formatDate(leadForm.eventDate, true)} extraClass="two-thirds" showNull={true} defaultNullValue="—" />
                    <DisplayValue label="Time" value={safeFormatTime(leadForm.eventTime)} extraClass="one-third" showNull={true} defaultNullValue="—" />
                    <DisplayValue label="Location" value={renderAddress()} showNull={true} defaultNullValue="—" />
                    <DisplayValue label="Service Style" value={serviceStyleName()} extraClass="half" showNull={true} defaultNullValue="—" />
                    <DisplayValue label="Cuisine Type" value={valueFromConfig(apiCuisines, leadForm.apiCuisineTypeId, 'name')} extraClass="half" showNull={true} defaultNullValue="—" />
                    <DisplayValue label="Message" value={leadForm.message} showNull={true} defaultNullValue="—" />
                </div>
            </div>
            <div className="actions">&nbsp;</div>
        </div>
    )

    // Multi-day bookings pick a style per meal instead of a single menu direction,
    // so they get a Format block rather than the Menu block.
    const renderFormatBlock = () => (
        <div className="event-block">
            <div className="block-type">
                <FontAwesomeIcon icon="drumstick-bite" /> The Format
            </div>
            <div className="content">
                <div className="displays">
                    <DisplayValue label="Kitchen & Cleanup" value={optionLabel(KITCHEN_MANAGEMENT_OPTIONS, leadForm.kitchenManagement)} showNull={true} defaultNullValue="—" />
                    <DisplayValue label="Additional Requests" value={leadForm.message} showNull={true} defaultNullValue="—" />
                </div>
            </div>
            <div className="actions">&nbsp;</div>
        </div>
    )

    const renderMenuBlock = () => {
        if (leadForm.formType === 'multi_day') {
            return renderFormatBlock()
        }
        if (leadForm.formType !== 'private_dinner') {
            return null
        }
        const vibe = leadForm.apiVibeId === 'other'
            ? leadForm.vibeOther
            : valueFromConfig(apiVibes, leadForm.apiVibeId, 'name')
        return (
            <div className="event-block">
                <div className="block-type">
                    <FontAwesomeIcon icon="drumstick-bite" /> Menu
                </div>
                <div className="content">
                    <div className="displays">
                        <DisplayValue label="Cuisine" value={valueFromConfig(apiCuisines, leadForm.apiCuisineTypeId, 'name')} extraClass="half" showNull={true} defaultNullValue="—" />
                        <DisplayValue label="Service Style" value={serviceStyleName()} extraClass="half" showNull={true} defaultNullValue="—" />
                        <DisplayValue label="Vibe" value={vibe} extraClass="half" showNull={true} defaultNullValue="—" />
                        <DisplayValue label="Bartender" value={optionLabel(NEEDS_BARTENDER_OPTIONS, leadForm.needsBartender)} extraClass="half" showNull={true} defaultNullValue="—" />
                        <DisplayValue label="Restaurant Comparison" value={leadForm.restaurantComparison} showNull={true} defaultNullValue="—" />
                        <DisplayValue label="Beverages" value={leadForm.beverages} showNull={true} defaultNullValue="—" />
                        <DisplayValue label="Additional Special Requests" value={leadForm.message} showNull={true} defaultNullValue="—" />
                    </div>
                </div>
                <div className="actions">&nbsp;</div>
            </div>
        )
    }

    // has_allergies only exists on leads submitted after the flag was added, and
    // form_type defaults every pre-existing row to private_dinner, so a null flag
    // means the lead still carries its detailed dietary answers.
    const hasAllergyFlag = () => leadForm.hasAllergies === true || leadForm.hasAllergies === false

    const hasLegacyDietaryDetail = () => (
        (leadForm.allergies || []).length > 0
            || (leadForm.dietaryPreferences || []).length > 0
            || Boolean(leadForm.allergyCount)
            || Boolean(leadForm.dislikes)
    )

    // Private dinner inquiries only capture a yes/no allergy flag up front — the
    // detailed breakdown is gathered in the secondary response form, so show it
    // alongside the flag once it has been filled in.
    const renderPrivateDinnerDietaryBlock = () => (
        <div className="event-block">
            <div className="block-type">
                <FontAwesomeIcon icon="seedling" /> Dietary
            </div>
            <div className="content">
                <div className="displays">
                    {(hasAllergyFlag() || !hasLegacyDietaryDetail()) && (
                        <DisplayValue label="Anyone with an Allergy" value={formatOption(leadForm.hasAllergies)} showNull={true} defaultNullValue="—" />
                    )}
                    {hasLegacyDietaryDetail() && (
                        <>
                            <DisplayValue label="Allergy Count" value={leadForm.allergyCount} extraClass="one-third" showNull={true} defaultNullValue="—" />
                            <DisplayValue label="Allergies" value={resolveList(leadForm.allergies, apiAllergens)} showNull={true} defaultNullValue="—" />
                            <DisplayValue label="Dietary Preferences" value={resolveList(leadForm.dietaryPreferences, apiDietaryRestrictions)} showNull={true} defaultNullValue="—" />
                            <DisplayValue label="Dislikes" value={leadForm.dislikes} showNull={true} defaultNullValue="—" />
                        </>
                    )}
                </div>
            </div>
            <div className="actions">&nbsp;</div>
        </div>
    )

    const renderDietaryBlock = () => (
        <div className="event-block">
            <div className="block-type">
                <FontAwesomeIcon icon="seedling" /> Dietary
            </div>
            <div className="content">
                <div className="displays">
                    <DisplayValue label="Allergy Count" value={leadForm.allergyCount} extraClass="one-third" showNull={true} defaultNullValue="—" />
                    <DisplayValue label="Allergies" value={resolveList(leadForm.allergies, apiAllergens)} showNull={true} defaultNullValue="—" />
                    <DisplayValue label="Dietary Preferences" value={resolveList(leadForm.dietaryPreferences, apiDietaryRestrictions)} showNull={true} defaultNullValue="—" />
                    <DisplayValue label="Dislikes" value={leadForm.dislikes} showNull={true} defaultNullValue="—" />
                    {leadForm.formType !== 'multi_day' && (
                        <DisplayValue label="Additional Notes" value={leadForm.message} showNull={true} defaultNullValue="—" />
                    )}
                </div>
            </div>
            <div className="actions">&nbsp;</div>
        </div>
    )

    const renderEventBlock = () => {
        if (leadForm.formType === 'private_dinner') {
            return renderPrivateDinnerBlock()
        }
        if (leadForm.formType === 'multi_day') {
            return renderMultiDayBlock()
        }
        if (leadForm.formType === 'weekly_meal') {
            return renderWeeklyMealBlock()
        }
        return renderLegacyBlock()
    }

    return (
        <>
            {renderEventBlock()}
            {renderMenuBlock()}
            {leadForm.formType === 'private_dinner' ? renderPrivateDinnerDietaryBlock() : renderDietaryBlock()}
        </>
    )
}

export default connect(mapStateToProps)(LeadEventInfo)
